/* eslint-disable */
import React, { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { Formik } from 'formik';
import { useMutation, useQuery } from 'react-fetching-library';
import { compare } from 'fast-json-patch';
import isEqual from 'lodash/isEqual';
import { makeStyles } from '@material-ui/core';
import ContentWrapper from 'components/Wrappers/ContentWrapper';
import RoundedButton from 'components/Shared/RoundedButton';
import showServerError from 'helpers/showError';
import OrderForm from 'components/Orders/OrderForm';
import useMetadataUpdate from 'hooks/useMetadataUpdate';
import BackButton from 'components/Shared/BackButton';
import { checkPermission } from 'helpers/checkPermissions';
import permissions from 'config/permissions';
import { ORDER_STATUS_OPTIONS } from 'components/Orders/constants';
import { useDispatch, useSelector } from 'react-redux';
import EmptyPageLoader from '../Shared/EmptyPageLoader';
import {
  enqueueSnackbar,
  SNACKBAR_VARIANTS,
} from '../../redux_store/reducer/reducers/notificationsReducer';
import { getTransitionWithVariable } from '../../redux_store/reducer/reducers/i18nReducer';
import { ALL_ROUTES } from '../../config/routes';
import { AppRouteContext } from '../../contexts';

const useStyles = makeStyles((theme) => ({
  contentContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    width: '100%',
  },
  formCard: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    background: '#ffffff',
    borderRadius: 16,
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
    overflow: 'hidden',
    marginBottom: theme.spacing(10),
    margin: theme.spacing(3, 0),
    [theme.breakpoints.down('sm')]: {
      margin: theme.spacing(2, 0),
      borderRadius: 12,
    },
  },
  scrollableContent: {
    flex: 1,
    overflow: 'auto',
    background: 'linear-gradient(135deg, #fafafa 0%, #ffffff 100%)',
  },
  formContent: {
    width: '100%',
  },
  actionsContainer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    width: '100%',
    padding: theme.spacing(3, 6, 4),
    background: '#ffffff',
    borderTop: '1px solid rgba(0, 0, 0, 0.12)',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: theme.spacing(2),
    boxShadow: '0 -4px 16px rgba(0, 0, 0, 0.08)',
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(2, 4, 3),
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2, 2, 3),
      gap: theme.spacing(1),
    },
  },
  actionButton: {
    textTransform: 'uppercase',
    borderRadius: 8,
    padding: theme.spacing(1.5, 2),
    letterSpacing: 1.5,
    fontWeight: 600,
    fontSize: 14,
    minWidth: 100,
    height: 40,
    [theme.breakpoints.down('sm')]: {
      minWidth: 100,
      padding: theme.spacing(1, 2),
      fontSize: 13,
    },
  },
  backButton: {
    marginRight: 'auto',
  },
}));

const OrderEdit = () => {
  const classes = useStyles();
  const { accountUuid, orderUuid, patientUuid } = useParams();
  const [backendErrors, setBackendErrors] = useState({});
  const [order, setOrder] = useState();
  const dispatch = useDispatch();
  const { setAppRoute } = useContext(AppRouteContext);
  const {
    user,
    metadata: {
      currentAccount,
      currentAccountPermissions,
      childAccountPermissions,
    },
    isDistributionSponsor,
  } = useSelector((state) => state.user);
  const {
    en: {
      titles,
      errors,
      notifications,
      btn,
    },
  } = useSelector((state) => state.i18n);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isFailed, setIsFailed] = useState(false);

  const { payload: metadata, error: metadataError, status } = useMetadataUpdate(
    useQuery,
    [
      {
        method: 'GET',
        endpoint: `/accounts/${accountUuid || currentAccount.uuid}/patients/${patientUuid}/create-order-metadata`,
      },
    ],
  );

  const onNotPermission = () => {
    window.history.back();
    dispatch(
      enqueueSnackbar({
        message: notifications.notPermissionForEditingOrder,
        options: {
          variant: SNACKBAR_VARIANTS.warning,
        },
      }),
    );
  };

  const { query } = useMetadataUpdate(useQuery, [
    {
      method: 'GET',
      endpoint: `/accounts/${accountUuid || currentAccount.uuid}/orders/${orderUuid}`,
    },
    false,
  ]);

  useEffect(() => {
    const hasPermission = checkPermission(
      !accountUuid
        ? currentAccountPermissions
        : childAccountPermissions,
      permissions.ORDER_PATCH,
    );
    if (!hasPermission) {
      onNotPermission();
    }
  }, []);

  useEffect(() => {
    if (status === 401) {
      onNotPermission();
    }
  }, [status]);

  useEffect(() => {
    query().then(({ error, payload }) => {
      if (!error) {
        setOrder(payload);
      }
    });
  }, [query]);

  const orderEditAction = (patches) => ({
    method: 'PATCH',
    endpoint: `/accounts/${accountUuid || currentAccount.uuid}/orders/${orderUuid}`,
    body: patches,
  });
  const { loading, mutate } = useMetadataUpdate(useMutation, [orderEditAction]);

  const editOrder = async (values, { setSubmitting, setFieldValue }) => {
    const {
      slitLowDose,
      slitLowDoseQuantity,
      slitMaintenance,
      slitMaintenanceQuantity,
    } = values;

    const formattedValues = {
      ...values,
      slitLowDoseQuantity:
        slitLowDose && slitLowDoseQuantity ? slitLowDoseQuantity : 0,
      slitMaintenanceQuantity:
        slitMaintenance && slitMaintenanceQuantity
          ? slitMaintenanceQuantity
          : 0,
    };
    const orderCopy = { ...order };
    delete formattedValues.scitAllergens; // will be patched manually
    delete formattedValues.slitLowDose; // internal usage
    delete formattedValues.slitMaintenance; // internal usage
    delete orderCopy.scitAllergens;
    delete orderCopy.slitLowDose;
    delete orderCopy.slitMaintenance;

    // compare all the values except deleted one
    const patches = compare(orderCopy, formattedValues);
    // create a patch for an array manually
    if (!isEqual(order.scitAllergens, values.scitAllergens)) {
      patches.push({
        op: 'replace',
        path: '/scitAllergens',
        value: values.scitAllergens,
      });
    }

    if (patches.length) {
      setBackendErrors({});
      const { payload, error, status } = await mutate(patches);
      if (error) {
        setIsFailed(true);
        setTimeout(() => {
          setIsFailed(false);
        }, 2000);
        if (!slitLowDose) {
          setFieldValue('slitLowDoseQuantity', 0);
        }
        if (!slitMaintenance) {
          setFieldValue('slitMaintenanceQuantity', 0);
        }

        const options = payload && {
          correlationUUID: payload.correlationUUID,
          userUUID: user.uuid,
        };
        switch (status) {
          case 422: {
            showServerError(dispatch, errors.validationError);
            setBackendErrors(payload.validationErrors);
            break;
          }
          default: {
            showServerError(dispatch, '', options);
            break;
          }
        }
      } else if (payload) {
        setIsSuccess(true);
        setTimeout(() => {
          setIsSuccess(false);
        }, 2000);
        setOrder(payload);
        dispatch(
          enqueueSnackbar({
            message: getTransitionWithVariable(notifications.orderEdited, {
              name: metadata.patientInfo.name,
            }),
            options: {
              variant: SNACKBAR_VARIANTS.success,
            },
          }),
        );
        setAppRoute({
            path: ALL_ROUTES.order.list.path,
            title: ALL_ROUTES.order.list.additional.title,
            params: {
              currentAccountUuid: currentAccount.uuid,
            },
            clearBreadcrumbs: true,
          },
          {
            addDelay: true,
          },
        );
      }
      setSubmitting(false);
    }
  };

  if (!order || !metadata || metadataError) {
    return <EmptyPageLoader />;
  }

  const isEditable = isDistributionSponsor
    || ORDER_STATUS_OPTIONS
    .find((orderStatus) => orderStatus.value === order.status)
    && ORDER_STATUS_OPTIONS
    .find((orderStatus) => orderStatus.value === order.status).allowEditOrder;

  return (
    <Formik
      initialValues={{
        ...order,
        slitLowDose: !!order.slitLowDoseQuantity,
        slitMaintenance: !!order.slitMaintenanceQuantity,
      }}
      validateOnBlur={false}
      enableReinitialize
      onSubmit={editOrder}
    >
      {({ isValid, isSubmitting, dirty, handleSubmit, values }) => (
        <ContentWrapper
          titleText={titles.editOrder}
          containerStyle={{ overflow: 'visible' }}
        >
          <div className={classes.contentContainer}>
            <div className={classes.formCard}>
              <div className={classes.scrollableContent}>
                <div className={classes.formContent}>
                  <OrderForm
                    values={values}
                    metadata={metadata}
                    backendErrors={backendErrors}
                    isEditable={isEditable}
                  />
                </div>
              </div>
            </div>
            <div className={classes.actionsContainer}>
              <BackButton className={classes.backButton} />
              <RoundedButton
                type='submit'
                variant='contained'
                color='primary'
                disabled={!isValid || isSubmitting || !dirty || !isEditable}
                onClick={handleSubmit}
                isLoading={loading}
                isSuccess={isSuccess}
                isFailed={isFailed}
                className={classes.actionButton}
              >
                {btn.save}
              </RoundedButton>
            </div>
          </div>
        </ContentWrapper>
      )}
    </Formik>
  );
};

export default OrderEdit;
